import {
    faClock,
    faLocationArrow,
    faLocationDot,
} from '@fortawesome/pro-regular-svg-icons';
import { usePlacesAutocomplete } from '../../hooks/usePlacesAutoComplete';
import {
    AutocompleteOption,
    AutocompleteTypes,
    DeviceGeolocationStatus,
    getPageUrl,
    TypeAheadInput,
    useAnalyticsClickEvent,
    useAnalyticsFilterEvent,
    useDeviceGeolocation,
    useMap,
} from 'rwl-ui';
import { useEffect, useState } from 'react';
import { useGlobal } from '../../hooks/useGlobal';
import { ClickTargets } from 'rwl-shared/dist/models/analytics';
import { useMapContext } from '../../hooks/MapProvider';

export const SearchBar = (): JSX.Element => {
    const { setApiKey } = useMap();
    const [featureLayer] = useState<google.maps.FeatureLayer | null>(null);
    const { config } = useGlobal();
    const sendClickEvent = useAnalyticsClickEvent();
    const sendFilterEvent = useAnalyticsFilterEvent();
    const { setNeighborhood } = useMapContext();

    const {
        setQuery,
        suggestions,
        recentResults,
        addRecentResult,
        setSelectedPlaceId,
        clearSelectedPlaceId,
        selectedPlaceDetails,
    } = usePlacesAutocomplete();
    const clearFeatureLayer = () => {
        if (featureLayer) {
            featureLayer.style = undefined;
        }
    };
    const { requestGeolocation } = useDeviceGeolocation();

    const getPlaceIdFromCurrentLocation = () => {
        requestGeolocation((location) => {
            if (location) {
                const geocodingService = new google.maps.Geocoder();
                geocodingService.geocode(
                    {
                        location: {
                            lat: location.coords.latitude,
                            lng: location.coords.longitude,
                        },
                    },
                    (results, status) => {
                        if (
                            status === google.maps.GeocoderStatus.OK &&
                            results?.[0]
                        ) {
                            const placeId = results[0].place_id;
                            setSelectedPlaceId(placeId);
                        }
                    }
                );
            }
        });
    };

    useEffect(() => {
        if (config && config.googleMapsKey) {
            setApiKey(config.googleMapsKey);
        }
    }, [config]);

    useEffect(() => {
        if (selectedPlaceDetails) {
            const postalComponent =
                selectedPlaceDetails.address_components?.find((component) =>
                    component.types.includes('postal_code')
                );
            if (postalComponent?.short_name) {
                const zipcode = postalComponent.short_name;
                sendFilterEvent({
                    filter: zipcode,
                    wasAdded: true,
                });
            }
        }
    }, [selectedPlaceDetails]);

    const locationSuggestions: AutocompleteOption[] = [
        ...recentResults.map((r) => ({
            type: AutocompleteTypes.RECENT,
            key: r.place_id || r.description,
            label: r.description,
            icon: faClock,
            onSelected: () => {
                clearFeatureLayer();
                setSelectedPlaceId(r.place_id);
                sendClickEvent({
                    url: getPageUrl(),
                    target: ClickTargets.SEARCH_FINISHED,
                });
            },
        })),
        ...suggestions.map((s) => ({
            type: AutocompleteTypes.SUGGESTION,
            key: s.place_id || s.description,
            label: s.description,
            icon: faLocationDot,
            onSelected: () => {
                clearFeatureLayer();
                addRecentResult(s);
                setSelectedPlaceId(s.place_id);
                sendClickEvent({
                    url: getPageUrl(),
                    target: ClickTargets.SEARCH_FINISHED,
                });
            },
        })),
    ];

    if (status !== DeviceGeolocationStatus.Unavailable) {
        locationSuggestions.unshift({
            type: AutocompleteTypes.CURRENT,
            key: 'current',
            label: 'Current location',
            icon: faLocationArrow,
            onSelected: () => {
                clearFeatureLayer();
                sendClickEvent({
                    url: getPageUrl(),
                    target: ClickTargets.SEARCH_FINISHED,
                });
                getPlaceIdFromCurrentLocation();
            },
        });
    }
    return (
        <div style={{ width: '100%' }}>
            <TypeAheadInput
                smallSize={false}
                prefilledValue={selectedPlaceDetails?.formatted_address}
                suggestions={locationSuggestions}
                onTyping={(value) => {
                    if (value.length > 3) {
                        setQuery(value);
                    }
                }}
                onClear={() => {
                    clearSelectedPlaceId();
                    clearFeatureLayer();
                    setNeighborhood(undefined);
                }}
                onFocus={() => {
                    sendClickEvent({
                        url: getPageUrl(),
                        target: ClickTargets.SEARCH_FOCUSED,
                    });
                }}
            />
        </div>
    );
};
